import { z } from "zod";

// Single source of truth for every enum. Stored as zod-checked text (not Postgres
// enums) so adding a stage/status later isn't a migration — see .planning/01-data-model.md §5.

/** Which client view a search belongs to. */
export const TierSchema = z.enum(["csuite", "vp_director", "general"]);
export type Tier = z.infer<typeof TierSchema>;

/** Search lifecycle. */
export const SearchStatusSchema = z.enum(["active", "on_hold", "closed"]);
export type SearchStatus = z.infer<typeof SearchStatusSchema>;

/** Default pipeline ladder (PRD §5.2). Per-search overridable via stage_config. */
export const StageSchema = z.enum([
  "longlist",
  "briefing",
  "round_1",
  "round_2",
  "final",
  "reference_check",
  "offer",
  "placed",
]);
export type Stage = z.infer<typeof StageSchema>;

/** Ordered ladder for "furthest stage" / progress math. Index = depth. */
export const STAGE_ORDER: Stage[] = [
  "longlist",
  "briefing",
  "round_1",
  "round_2",
  "final",
  "reference_check",
  "offer",
  "placed",
];

export const STAGE_LABELS: Record<Stage, string> = {
  longlist: "Longlist",
  briefing: "Briefing",
  round_1: "Round 1",
  round_2: "Round 2",
  final: "Final",
  reference_check: "Reference Check",
  offer: "Offer",
  placed: "Placed",
};

/** Status of a candidate *on a particular search* (lives on the edge, not the person). */
export const CandidateStatusSchema = z.enum(["active", "on_hold", "withdrawn", "rejected", "placed"]);
export type CandidateStatus = z.infer<typeof CandidateStatusSchema>;

/** Interviewer recommendation per round. */
export const RecommendationSchema = z.enum(["proceed", "hold", "reject"]);
export type Recommendation = z.infer<typeof RecommendationSchema>;

export const RECOMMENDATION_LABELS: Record<Recommendation, string> = {
  proceed: "Proceed",
  hold: "Hold",
  reject: "Reject",
};

/** Interview format. */
export const InterviewFormatSchema = z.enum(["video", "in_person", "panel"]);
export type InterviewFormat = z.infer<typeof InterviewFormatSchema>;

export const INTERVIEW_FORMAT_LABELS: Record<InterviewFormat, string> = {
  video: "Video",
  in_person: "In person",
  panel: "Panel",
};

/** Status of a single opening (seat) within a role. `open` ⇒ still being filled; `filled` ⇒ a
 *  candidate won it; `on_hold`/`cancelled` ⇒ paused or dropped (excluded from the "to fill" count). */
export const OpeningStatusSchema = z.enum(["open", "filled", "on_hold", "cancelled"]);
export type OpeningStatus = z.infer<typeof OpeningStatusSchema>;

export const OPENING_STATUS_LABELS: Record<OpeningStatus, string> = {
  open: "Open",
  filled: "Filled",
  on_hold: "On hold",
  cancelled: "Cancelled",
};

/** Document kind. */
export const DocTypeSchema = z.enum(["cv", "assessment", "reference", "offer_letter", "other"]);
export type DocType = z.infer<typeof DocTypeSchema>;

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  cv: "CV",
  assessment: "Assessment",
  reference: "Reference",
  offer_letter: "Offer letter",
  other: "Other",
};

/** User roles. `restricted_viewer` (PII-redacted) is optional — enforced by the future auth plan. */
export const RoleSchema = z.enum(["admin", "client_viewer", "restricted_viewer"]);
export type Role = z.infer<typeof RoleSchema>;

/** Audit action. */
export const AuditActionSchema = z.enum(["create", "update", "delete"]);
export type AuditAction = z.infer<typeof AuditActionSchema>;

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
};
